import { 
  Store, Package, ShoppingCart, 
  Eye, Clock, CheckCircle, 
  Building2, Hash, Car, 
  Wrench, Phone, Archive,
  Users, DollarSign
} from 'lucide-react';

// Store type configurations with their specific fields
export const storeTypeConfigs = {
  real_estate: {
    name: 'عقارات',
    nameEn: 'Real Estate',
    icon: Building2,
    color: 'from-blue-500 to-cyan-500',
    itemName: 'عقار',
    itemNameEn: 'Property',
    itemsName: 'العقارات',
    itemsNameEn: 'Properties',
    hasNegotiable: true,
    fields: [
      { id: 'listingType', label: 'نوع الإعلان', labelEn: 'Listing Type', type: 'select', required: true, options: [
        { value: 'for_sale', label: 'للبيع', labelEn: 'For Sale' },
        { value: 'for_rent', label: 'للإيجار', labelEn: 'For Rent' },
        { value: 'for_investment', label: 'للاستثمار', labelEn: 'For Investment' }
      ]},
      { id: 'title', label: 'عنوان العقار', labelEn: 'Property Title', type: 'text', required: true },
      { id: 'propertyType', label: 'نوع العقار', labelEn: 'Property Type', type: 'select', required: true, options: [
        { value: 'apartment', label: 'شقة', labelEn: 'Apartment' },
        { value: 'villa', label: 'فيلا', labelEn: 'Villa' },
        { value: 'townhouse', label: 'تاون هاوس', labelEn: 'Townhouse' },
        { value: 'penthouse', label: 'بنتهاوس', labelEn: 'Penthouse' },
        { value: 'studio', label: 'استوديو', labelEn: 'Studio' },
        { value: 'land', label: 'أرض', labelEn: 'Land' },
        { value: 'building', label: 'مبنى', labelEn: 'Building' },
        { value: 'commercial', label: 'محل تجاري', labelEn: 'Commercial' },
        { value: 'office', label: 'مكتب', labelEn: 'Office' },
        { value: 'warehouse', label: 'مستودع', labelEn: 'Warehouse' }
      ]},
      { id: 'emirate', label: 'الإمارة', labelEn: 'Emirate', type: 'select', required: true, options: [
        { value: 'abu_dhabi', label: 'أبوظبي', labelEn: 'Abu Dhabi' },
        { value: 'dubai', label: 'دبي', labelEn: 'Dubai' },
        { value: 'sharjah', label: 'الشارقة', labelEn: 'Sharjah' },
        { value: 'ajman', label: 'عجمان', labelEn: 'Ajman' },
        { value: 'ras_al_khaimah', label: 'رأس الخيمة', labelEn: 'Ras Al Khaimah' },
        { value: 'fujairah', label: 'الفجيرة', labelEn: 'Fujairah' },
        { value: 'umm_al_quwain', label: 'أم القيوين', labelEn: 'Umm Al Quwain' }
      ]},
      { id: 'area_name', label: 'المنطقة', labelEn: 'Area/District', type: 'text', required: true },
      { id: 'building_name', label: 'اسم المبنى/المشروع', labelEn: 'Building/Project Name', type: 'text' },
      { id: 'area', label: 'المساحة (قدم مربع)', labelEn: 'Area (sq ft)', type: 'number', required: true },
      { id: 'bedrooms', label: 'غرف النوم', labelEn: 'Bedrooms', type: 'select', options: [
        { value: 'studio', label: 'استوديو', labelEn: 'Studio' },
        { value: '1', label: '1', labelEn: '1' },
        { value: '2', label: '2', labelEn: '2' },
        { value: '3', label: '3', labelEn: '3' },
        { value: '4', label: '4', labelEn: '4' },
        { value: '5', label: '5', labelEn: '5' },
        { value: '6', label: '6', labelEn: '6' },
        { value: '7+', label: '7+', labelEn: '7+' }
      ]},
      { id: 'bathrooms', label: 'الحمامات', labelEn: 'Bathrooms', type: 'select', options: [
        { value: '1', label: '1', labelEn: '1' },
        { value: '2', label: '2', labelEn: '2' },
        { value: '3', label: '3', labelEn: '3' },
        { value: '4', label: '4', labelEn: '4' },
        { value: '5', label: '5', labelEn: '5' },
        { value: '6+', label: '6+', labelEn: '6+' }
      ]},
      { id: 'parking', label: 'مواقف السيارات', labelEn: 'Parking Spaces', type: 'select', options: [
        { value: '0', label: 'لا يوجد', labelEn: 'None' },
        { value: '1', label: '1', labelEn: '1' },
        { value: '2', label: '2', labelEn: '2' },
        { value: '3', label: '3', labelEn: '3' },
        { value: '4+', label: '4+', labelEn: '4+' }
      ]},
      { id: 'floor', label: 'الطابق', labelEn: 'Floor', type: 'text' },
      
      // === حقول خاصة بالبيع ===
      { id: 'price', label: 'السعر (درهم)', labelEn: 'Price (AED)', type: 'number', required: true, showFor: 'for_sale' },
      { id: 'ownership', label: 'نوع التملك', labelEn: 'Ownership Type', type: 'select', showFor: 'for_sale', options: [
        { value: 'freehold', label: 'تملك حر', labelEn: 'Freehold' },
        { value: 'leasehold', label: 'إيجار طويل', labelEn: 'Leasehold' }
      ]},
      { id: 'completion', label: 'حالة العقار', labelEn: 'Completion Status', type: 'select', showFor: 'for_sale', options: [
        { value: 'ready', label: 'جاهز للسكن', labelEn: 'Ready to Move' },
        { value: 'off_plan', label: 'على الخارطة', labelEn: 'Off-Plan' }
      ]},
      { id: 'handover_date', label: 'تاريخ التسليم', labelEn: 'Handover Date', type: 'text', showFor: 'for_sale', placeholder: 'مثال: Q4 2026' },
      { id: 'service_charge', label: 'رسوم الخدمات السنوية (درهم)', labelEn: 'Service Charge/Year (AED)', type: 'number', showFor: 'for_sale' },
      { id: 'year_built', label: 'سنة البناء', labelEn: 'Year Built', type: 'number', showFor: 'for_sale' },
      
      // === حقول خاصة بالإيجار ===
      { id: 'rent_price', label: 'الإيجار السنوي (درهم)', labelEn: 'Yearly Rent (AED)', type: 'number', required: true, showFor: 'for_rent' },
      { id: 'rent_frequency', label: 'دورة الدفع', labelEn: 'Rent Frequency', type: 'select', showFor: 'for_rent', options: [
        { value: 'yearly', label: 'سنوي', labelEn: 'Yearly' },
        { value: 'monthly', label: 'شهري', labelEn: 'Monthly' }
      ]},
      { id: 'cheques', label: 'عدد الشيكات', labelEn: 'Number of Cheques', type: 'select', showFor: 'for_rent', options: [
        { value: '1', label: '1 شيك', labelEn: '1 Cheque' },
        { value: '2', label: '2 شيكات', labelEn: '2 Cheques' },
        { value: '3', label: '3 شيكات', labelEn: '3 Cheques' },
        { value: '4', label: '4 شيكات', labelEn: '4 Cheques' },
        { value: '6', label: '6 شيكات', labelEn: '6 Cheques' },
        { value: '12', label: '12 شيك', labelEn: '12 Cheques' }
      ]},
      { id: 'furnished', label: 'التأثيث', labelEn: 'Furnishing', type: 'select', showFor: 'for_rent', options: [
        { value: 'furnished', label: 'مفروش', labelEn: 'Furnished' },
        { value: 'semi_furnished', label: 'مفروش جزئياً', labelEn: 'Semi-Furnished' },
        { value: 'unfurnished', label: 'غير مفروش', labelEn: 'Unfurnished' }
      ]},
      { id: 'bills_included', label: 'الفواتير', labelEn: 'Bills', type: 'select', showFor: 'for_rent', options: [
        { value: 'included', label: 'شامل الفواتير', labelEn: 'Bills Included' },
        { value: 'excluded', label: 'غير شامل', labelEn: 'Bills Excluded' }
      ]},
      { id: 'available_from', label: 'متاح من', labelEn: 'Available From', type: 'date', showFor: 'for_rent' },
      { id: 'min_contract', label: 'الحد الأدنى للعقد', labelEn: 'Min. Contract', type: 'select', showFor: 'for_rent', options: [
        { value: '1_month', label: 'شهر', labelEn: '1 Month' },
        { value: '3_months', label: '3 أشهر', labelEn: '3 Months' },
        { value: '6_months', label: '6 أشهر', labelEn: '6 Months' },
        { value: '1_year', label: 'سنة', labelEn: '1 Year' }
      ]},
      
      // === حقول خاصة بالاستثمار ===
      { id: 'investment_price', label: 'سعر العقار (درهم)', labelEn: 'Property Price (AED)', type: 'number', required: true, showFor: 'for_investment' },
      { id: 'expected_roi', label: 'العائد المتوقع (%)', labelEn: 'Expected ROI (%)', type: 'number', showFor: 'for_investment', placeholder: 'مثال: 8' },
      { id: 'rental_yield', label: 'العائد الإيجاري السنوي (درهم)', labelEn: 'Annual Rental Yield (AED)', type: 'number', showFor: 'for_investment' },
      { id: 'occupancy_rate', label: 'نسبة الإشغال (%)', labelEn: 'Occupancy Rate (%)', type: 'number', showFor: 'for_investment', placeholder: 'مثال: 90' },
      { id: 'investment_type', label: 'نوع الاستثمار', labelEn: 'Investment Type', type: 'select', showFor: 'for_investment', options: [
        { value: 'residential', label: 'سكني', labelEn: 'Residential' },
        { value: 'commercial', label: 'تجاري', labelEn: 'Commercial' },
        { value: 'mixed_use', label: 'متعدد الاستخدام', labelEn: 'Mixed Use' },
        { value: 'hotel_apartment', label: 'شقة فندقية', labelEn: 'Hotel Apartment' }
      ]},
      { id: 'tenant_status', label: 'حالة المستأجر', labelEn: 'Tenant Status', type: 'select', showFor: 'for_investment', options: [
        { value: 'occupied', label: 'مؤجر حالياً', labelEn: 'Currently Occupied' },
        { value: 'vacant', label: 'شاغر', labelEn: 'Vacant' }
      ]},
      { id: 'lease_end_date', label: 'تاريخ انتهاء العقد', labelEn: 'Lease End Date', type: 'date', showFor: 'for_investment' },
      { id: 'current_rent', label: 'الإيجار الحالي (درهم/سنة)', labelEn: 'Current Rent (AED/Year)', type: 'number', showFor: 'for_investment' },
      
      // === حقول مشتركة ===
      { id: 'amenities', label: 'المرافق والخدمات', labelEn: 'Amenities', type: 'multiselect', options: [
        { value: 'pool', label: 'مسبح', labelEn: 'Pool' },
        { value: 'gym', label: 'صالة رياضية', labelEn: 'Gym' },
        { value: 'security', label: 'أمن 24 ساعة', labelEn: '24h Security' },
        { value: 'balcony', label: 'بلكونة', labelEn: 'Balcony' },
        { value: 'maid_room', label: 'غرفة خادمة', labelEn: 'Maid Room' },
        { value: 'storage', label: 'غرفة تخزين', labelEn: 'Storage Room' },
        { value: 'central_ac', label: 'تكييف مركزي', labelEn: 'Central A/C' },
        { value: 'covered_parking', label: 'موقف مغطى', labelEn: 'Covered Parking' },
        { value: 'pets_allowed', label: 'يسمح بالحيوانات', labelEn: 'Pets Allowed' },
        { value: 'sea_view', label: 'إطلالة بحرية', labelEn: 'Sea View' },
        { value: 'garden', label: 'حديقة', labelEn: 'Garden' },
        { value: 'private_pool', label: 'مسبح خاص', labelEn: 'Private Pool' }
      ]},
      { id: 'negotiable', label: 'قابل للتفاوض', labelEn: 'Negotiable', type: 'negotiable' },
      { id: 'description', label: 'الوصف', labelEn: 'Description', type: 'textarea' }
    ],
    stats: [
      { id: 'listings', label: 'العقارات', labelEn: 'Listings', icon: Building2 },
      { id: 'views', label: 'المشاهدات', labelEn: 'Views', icon: Eye },
      { id: 'inquiries', label: 'الاستفسارات', labelEn: 'Inquiries', icon: Phone },
      { id: 'sold', label: 'المباعة', labelEn: 'Sold', icon: CheckCircle }
    ]
  },
  numbers: {
    name: 'أرقام',
    nameEn: 'Numbers',
    icon: Hash,
    color: 'from-[#1E9BD9] to-[#0D7AB5]',
    itemName: 'رقم',
    itemNameEn: 'Number',
    itemsName: 'الأرقام',
    itemsNameEn: 'Numbers',
    fields: [
      { id: 'number', label: 'الرقم', labelEn: 'Number', type: 'text', required: true },
      { id: 'operator', label: 'المشغل', labelEn: 'Operator', type: 'select', options: [
        { value: 'etisalat', label: 'اتصالات', labelEn: 'Etisalat' },
        { value: 'du', label: 'دو', labelEn: 'Du' }
      ]},
      { id: 'category', label: 'الفئة', labelEn: 'Category', type: 'select', options: [
        { value: 'vip', label: 'VIP', labelEn: 'VIP' },
        { value: 'golden', label: 'ذهبي', labelEn: 'Golden' },
        { value: 'silver', label: 'فضي', labelEn: 'Silver' },
        { value: 'normal', label: 'عادي', labelEn: 'Normal' }
      ]},
      { id: 'price', label: 'السعر', labelEn: 'Price', type: 'number', required: true },
      { id: 'description', label: 'الوصف', labelEn: 'Description', type: 'textarea' }
    ],
    stats: [
      { id: 'available', label: 'متاح', labelEn: 'Available', icon: Hash },
      { id: 'sold', label: 'مباع', labelEn: 'Sold', icon: CheckCircle },
      { id: 'views', label: 'المشاهدات', labelEn: 'Views', icon: Eye },
      { id: 'inquiries', label: 'الاستفسارات', labelEn: 'Inquiries', icon: Phone }
    ]
  },
  phone_numbers: {
    name: 'أرقام الهواتف',
    nameEn: 'Phone Numbers',
    icon: Hash,
    color: 'from-[#1E9BD9] to-[#0D7AB5]',
    itemName: 'رقم هاتف',
    itemNameEn: 'Phone Number',
    itemsName: 'أرقام الهواتف',
    itemsNameEn: 'Phone Numbers',
    customForm: true,
    noImageUpload: true,
    fields: [],
    stats: [
      { id: 'available', label: 'أرقام متاحة', labelEn: 'Available', icon: Hash },
      { id: 'sold', label: 'أرقام مباعة', labelEn: 'Sold', icon: CheckCircle },
      { id: 'archived', label: 'مؤرشفة', labelEn: 'Archived', icon: Archive },
      { id: 'inquiries', label: 'الاستفسارات', labelEn: 'Inquiries', icon: Phone }
    ]
  },
  car_plates: {
    name: 'أرقام السيارات',
    nameEn: 'Car Plates',
    icon: Car,
    color: 'from-amber-500 to-yellow-500',
    itemName: 'لوحة سيارة',
    itemNameEn: 'Car Plate',
    itemsName: 'لوحات السيارات',
    itemsNameEn: 'Car Plates',
    noImageUpload: true,
    showPlatePreview: true,
    useDynamicPlateForm: true,
    fields: [
      { id: 'plateNumber', label: 'رقم اللوحة', labelEn: 'Plate Number', type: 'text', required: true },
      { id: 'emirate', label: 'الإمارة', labelEn: 'Emirate', type: 'select', required: true, options: [
        { value: 'dubai', label: 'دبي', labelEn: 'Dubai' },
        { value: 'abu_dhabi', label: 'أبوظبي', labelEn: 'Abu Dhabi' },
        { value: 'sharjah', label: 'الشارقة', labelEn: 'Sharjah' },
        { value: 'ajman', label: 'عجمان', labelEn: 'Ajman' },
        { value: 'rak', label: 'رأس الخيمة', labelEn: 'Ras Al Khaimah' },
        { value: 'fujairah', label: 'الفجيرة', labelEn: 'Fujairah' },
        { value: 'umm_al_quwain', label: 'أم القيوين', labelEn: 'Umm Al Quwain' }
      ]},
      { id: 'plateType', label: 'صنف اللوحة', labelEn: 'Plate Type', type: 'select', required: true, options: [
        { value: 'private', label: 'خصوصي', labelEn: 'Private' },
        { value: 'motorcycle', label: 'دراجة', labelEn: 'Motorcycle' },
        { value: 'classic', label: 'كلاسيك', labelEn: 'Classic' }
      ]},
      { id: 'plateCode', label: 'كود اللوحة', labelEn: 'Plate Code', type: 'dynamic_select', required: true },
      { id: 'price', label: 'السعر', labelEn: 'Price', type: 'number', required: true },
      { id: 'negotiable', label: 'قابل للتفاوض', labelEn: 'Negotiable', type: 'negotiable' },
      { id: 'description', label: 'ملاحظات', labelEn: 'Notes', type: 'textarea' }
    ],
    stats: [
      { id: 'available', label: 'لوحات متاحة', labelEn: 'Available', icon: Car },
      { id: 'sold', label: 'لوحات مباعة', labelEn: 'Sold', icon: CheckCircle },
      { id: 'views', label: 'المشاهدات', labelEn: 'Views', icon: Eye },
      { id: 'inquiries', label: 'الاستفسارات', labelEn: 'Inquiries', icon: Phone }
    ]
  },
  spare_parts: {
    name: 'قطع غيار',
    nameEn: 'Spare Parts',
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    itemName: 'قطعة',
    itemNameEn: 'Part',
    itemsName: 'القطع',
    itemsNameEn: 'Parts',
    fields: [
      { id: 'name', label: 'اسم القطعة', labelEn: 'Part Name', type: 'text', required: true },
      { id: 'partNumber', label: 'رقم القطعة', labelEn: 'Part Number', type: 'text' },
      { id: 'brand', label: 'الماركة', labelEn: 'Brand', type: 'text' },
      { id: 'carModel', label: 'موديل السيارة', labelEn: 'Car Model', type: 'text' },
      { id: 'condition', label: 'الحالة', labelEn: 'Condition', type: 'select', options: [
        { value: 'new', label: 'جديد', labelEn: 'New' },
        { value: 'used', label: 'مستعمل', labelEn: 'Used' },
        { value: 'refurbished', label: 'مجدد', labelEn: 'Refurbished' }
      ]},
      { id: 'quantity', label: 'الكمية', labelEn: 'Quantity', type: 'number' },
      { id: 'price', label: 'السعر', labelEn: 'Price', type: 'number', required: true },
      { id: 'description', label: 'الوصف', labelEn: 'Description', type: 'textarea' }
    ],
    stats: [
      { id: 'products', label: 'المنتجات', labelEn: 'Products', icon: Package },
      { id: 'orders', label: 'الطلبات', labelEn: 'Orders', icon: ShoppingCart },
      { id: 'sales', label: 'المبيعات', labelEn: 'Sales', icon: DollarSign },
      { id: 'customers', label: 'العملاء', labelEn: 'Customers', icon: Users }
    ]
  },
  auto: {
    name: 'سيارات',
    nameEn: 'Auto',
    icon: Car,
    color: 'from-green-500 to-emerald-500',
    itemName: 'سيارة',
    itemNameEn: 'Vehicle',
    itemsName: 'السيارات',
    itemsNameEn: 'Vehicles',
    hasNegotiable: true,
    multipleImages: true,
    maxImages: 5,
    fields: [
      { id: 'make', label: 'الشركة المصنعة', labelEn: 'Make', type: 'text', required: true },
      { id: 'model', label: 'الموديل', labelEn: 'Model', type: 'text', required: true },
      { id: 'year', label: 'سنة الصنع', labelEn: 'Year', type: 'year_select', required: true },
      { id: 'origin', label: 'المنشأ', labelEn: 'Origin', type: 'radio', required: true, options: [
        { value: 'imported', label: 'وارد', labelEn: 'Imported' },
        { value: 'gcc', label: 'خليجي', labelEn: 'GCC' }
      ]},
      { id: 'doors', label: 'عدد الأبواب', labelEn: 'Number of Doors', type: 'select', options: [
        { value: '2', label: '2', labelEn: '2' },
        { value: '3', label: '3', labelEn: '3' },
        { value: '4', label: '4', labelEn: '4' },
        { value: '5', label: '5', labelEn: '5' }
      ]},
      { id: 'color', label: 'اللون', labelEn: 'Color', type: 'select', options: [
        { value: 'white', label: 'أبيض', labelEn: 'White' },
        { value: 'black', label: 'أسود', labelEn: 'Black' },
        { value: 'silver', label: 'فضي', labelEn: 'Silver' },
        { value: 'gray', label: 'رمادي', labelEn: 'Gray' },
        { value: 'red', label: 'أحمر', labelEn: 'Red' },
        { value: 'blue', label: 'أزرق', labelEn: 'Blue' },
        { value: 'green', label: 'أخضر', labelEn: 'Green' },
        { value: 'brown', label: 'بني', labelEn: 'Brown' },
        { value: 'beige', label: 'بيج', labelEn: 'Beige' },
        { value: 'gold', label: 'ذهبي', labelEn: 'Gold' },
        { value: 'other', label: 'أخرى', labelEn: 'Other' }
      ]},
      { id: 'cylinders', label: 'السلندر', labelEn: 'Cylinders', type: 'select', options: [
        { value: '3', label: '3 سلندر', labelEn: '3 Cylinders' },
        { value: '4', label: '4 سلندر', labelEn: '4 Cylinders' },
        { value: '6', label: '6 سلندر', labelEn: '6 Cylinders' },
        { value: '8', label: '8 سلندر', labelEn: '8 Cylinders' },
        { value: '10', label: '10 سلندر', labelEn: '10 Cylinders' },
        { value: '12', label: '12 سلندر', labelEn: '12 Cylinders' },
        { value: 'electric', label: 'كهربائي', labelEn: 'Electric' }
      ]},
      { id: 'mileage', label: 'المسافة المقطوعة (كم)', labelEn: 'Mileage (km)', type: 'number' },
      { id: 'condition', label: 'الحالة', labelEn: 'Condition', type: 'radio', required: true, options: [
        { value: 'new', label: 'جديد', labelEn: 'New' },
        { value: 'used', label: 'مستعمل', labelEn: 'Used' }
      ]},
      { id: 'price', label: 'السعر', labelEn: 'Price', type: 'number', required: true },
      { id: 'negotiable', label: 'قابل للتفاوض', labelEn: 'Negotiable', type: 'negotiable' },
      { id: 'description', label: 'الوصف', labelEn: 'Description', type: 'textarea' }
    ],
    stats: [
      { id: 'vehicles', label: 'السيارات', labelEn: 'Vehicles', icon: Car },
      { id: 'sold', label: 'المباعة', labelEn: 'Sold', icon: CheckCircle },
      { id: 'views', label: 'المشاهدات', labelEn: 'Views', icon: Eye },
      { id: 'inquiries', label: 'الاستفسارات', labelEn: 'Inquiries', icon: Phone }
    ]
  },
  ecommerce: {
    name: 'تجارة إلكترونية',
    nameEn: 'E-Commerce',
    icon: Store,
    color: 'from-cyan-500 to-blue-500',
    itemName: 'منتج',
    itemNameEn: 'Product',
    itemsName: 'المنتجات',
    itemsNameEn: 'Products',
    fields: [
      { id: 'name', label: 'اسم المنتج', labelEn: 'Product Name', type: 'text', required: true },
      { id: 'sku', label: 'رمز المنتج (SKU)', labelEn: 'SKU', type: 'text' },
      { id: 'category', label: 'الفئة', labelEn: 'Category', type: 'select', options: [
        { value: 'perfumes', label: 'عطور', labelEn: 'Perfumes' },
        { value: 'clothes', label: 'ملابس', labelEn: 'Clothes' },
        { value: 'electronics', label: 'إلكترونيات', labelEn: 'Electronics' },
        { value: 'accessories', label: 'إكسسوارات', labelEn: 'Accessories' },
        { value: 'beauty', label: 'مستحضرات تجميل', labelEn: 'Beauty' },
        { value: 'other', label: 'أخرى', labelEn: 'Other' }
      ]},
      { id: 'price', label: 'السعر', labelEn: 'Price', type: 'number', required: true },
      { id: 'salePrice', label: 'سعر التخفيض', labelEn: 'Sale Price', type: 'number' },
      { id: 'quantity', label: 'الكمية', labelEn: 'Quantity', type: 'number' },
      { id: 'description', label: 'الوصف', labelEn: 'Description', type: 'textarea' }
    ],
    stats: [
      { id: 'products', label: 'المنتجات', labelEn: 'Products', icon: Package },
      { id: 'orders', label: 'الطلبات', labelEn: 'Orders', icon: ShoppingCart },
      { id: 'sales', label: 'المبيعات', labelEn: 'Sales', icon: DollarSign },
      { id: 'customers', label: 'العملاء', labelEn: 'Customers', icon: Users }
    ]
  }
};

// Telecom logos for phone numbers display
export const telecomLogos = {
  etisalat: '/carrier-etisalat.png',
  du: '/carrier-du.png'
};
